import axios from 'axios'
import { DELIVERY_METHOD } from 'domains/seller-inbound/constants'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/sellers/inbounds'

const SellerInboundsApiV2 = {
  /**
   * Get list of Seller Inbound and its associations
   * Invoke `GET api/v2/admins/sellers/inbounds
   * @param {object} tableState - ReactTable final state
   * @param {object} additionalFilters - A plain object to get customize filters outside `tableState`
   */
  get: async ({
    //set default parameters
    page                  = 1,
    per_page              = 10,
    code                  = '',
    delivery_method       = '',
    requested_date        = '',
    received_status       = '',
    curation_status       = '',
    confirmation_status   = '',
    live_process_status   = '',
    received_date         = '',
    seller_type           = '',
    seller_name           = '',
    seller_code           = '',
    seller_email          = '',
    seller_mobile          = '',
    pickup_scheduled      = '',
    pickup_scheduled_date = '',
    gender                = '',
    notes                 = '',
    dropoff_location      = '',
    tl_brand_classification_name = ''
  }) => {
    let params = {
      page                         : page,
      per_page                     : per_page,
      code                         : code,
      delivery_method              : delivery_method,
      requested_date               : requested_date,
      received_status              : received_status,
      curation_status              : curation_status,
      confirmation_status          : confirmation_status,
      live_process_status          : live_process_status,
      received_date                : received_date,
      seller_type                  : seller_type,
      seller_name                  : seller_name,
      seller_code                  : seller_code,
      seller_email                 : seller_email,
      seller_mobile                : seller_mobile,
      pickup_scheduled             : pickup_scheduled,
      pickup_scheduled_date        : pickup_scheduled_date,
      gender                       : gender,
      notes                        : notes,
      dropoff_location             : dropoff_location,
      tl_brand_classification_name : tl_brand_classification_name
    }

    params = ParamsUtil.removeEmptyAttributes(params)

    const path = API_PATH

    const response = await axios.get(path, { params })
    return response
  },

  /**
   * Show Seller Inbound by specific id
   * Inovke `GET api/v2/admins//sellers/inbounds/:id`
   * @param {number} id Seller Inbound's ID
   */
  show: async (id) => {
    const path     = `${API_PATH}/${id}`
    const response = axios.get(path)
    return response
  },

  /**
   * Create inbound ednpoint.
   * Invoke `POST api/v2/admins/sellers/inbounds`
   * @param {object} inbound inboud object. It also include `pickup` properties
   */
  create: async ({
    seller_id,
    quantity,
    requested_date,
    delivery_method,
    gender,
    pickup,
    dropoff_location,
    notes
  }) => {
    const path                    = API_PATH
    const isDropoffDeliveryMethod = delivery_method === DELIVERY_METHOD.DROPOFF
    const dropoffLocation         = isDropoffDeliveryMethod ? dropoff_location : ''
    const body                    = {
      inbound: {
        seller_id        : seller_id,
        quantity         : quantity,
        requested_date   : requested_date,
        delivery_method  : delivery_method,
        gender           : gender,
        pickup           : pickup,
        notes            : notes,
        dropoff_location : dropoffLocation
      }
    }
    const response                = axios.post(path, body)
    return response
  },

  /**
   * Update Seller Inbound ednpoint.
   * Invoke `PATCH api/v2/admins/sellers/inbounds/:id`
   * @param {object} seller_inbound - Seller Inboud object
   * @param {id} seller_inbound.id - Seller Inbound's ID
   * @param {quantity} seller_inbound.quantity - Seller Inbound quantity
   * @param {Date} seller_inbound.requested_date - Seller Inbound requested date
   * @param {object} seller_inbound.pickup - Seller Inbound Pickup
   */
  update: async ({
    id,
    quantity,
    received_quantity,
    requested_date,
    pickup,
    gender
  }) => {
    const path = `${API_PATH}/${id}`
    const body = {
      inbound: {
        id                : id,
        quantity          : quantity,
        received_quantity : received_quantity,
        requested_date    : requested_date,
        pickup            : pickup,
        gender            : gender
      }
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Mark Seller Inbound to already received
   * Invoke `POST api/v2/admins/sellers/inbounds/receive`
   * @param {number} inbound_id Seller Inbound ID
   * @param {number} received_quantity Total quantity that already received by Ops
   * @param {number} weight total weight of the inbound
   */
  receive: async ({
    inbound_id,
    received_quantity,
    weight,
    tracking_no
  }) =>  {
    const path = `${API_PATH}/receive`
    const body = {
      inbound: {
        inbound_id        : inbound_id,
        received_quantity : received_quantity,
        weight            : weight,
        tracking_no       : tracking_no
      }
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Schedule pickup the Seller Inbound
   * Invoke `PATCH api/v2/admins/sellers/inbounds/pickup_schedule
   * @param {number} id Seller Inbound ID
   * @param {string} courier Which shipment courier that will be use to pickup
   * @param {number} tracking_no Tracking number of the shipment
   * @param {date, string} scheduled_at Pickup scheduling date
   */
  schedule_pickup: async ({
    id,
    courier,
    scheduled_at
  }) => {
    const path =  `${API_PATH}/schedule_pickup`
    const body = {
      inbound: {
        id     : id,
        pickup : {
          courier      : courier,
          scheduled_at : scheduled_at
        }
      }
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Make live all products in single Seller Inbound
   * Invoke `PATCH api/v2/admins/sellers/inbounds/make_live
   * @param {number} id Seller Inbound ID
   */
  make_live: async ({
    id
  }) => {
    const path =  `${API_PATH}/make_live`
    const body = {
      inbound: {
        id: id
      }
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Get list of Pending Seller Inbound Pickups as CSV
   * Invoke `POST api/v2/admins/sellers/inbounds/create_csv_pickup
   */
  getCsvPickup: async (params) => {
    const path = `${API_PATH}/create_csv_pickup`

    const body = {
      inbound: params
    }

    const response = await axios.post(path, body)
    return response
  }
}

export default SellerInboundsApiV2